/**
 * Portal detection and cross-portal linking.
 *
 * Neighborli ships a single React bundle that can host four portals:
 *   - "landing"    — public marketing site (www.neighborli.net)
 *   - "consumer"   — the main peer-to-peer sharing app (app.neighborli.net)
 *   - "admin"      — internal ops (admin.neighborli.net)
 *   - "advertiser" — 3rd-party advertiser self-service (advertiser.neighborli.net)
 *
 * The bundle can be deployed two ways:
 *
 * A. **Path-prefix mode** (current default for preview at emergent.host)
 *    All portals live under the same origin, differentiated by the URL path.
 *    `getPortal()` returns "consumer" by default and every route tree is
 *    mounted. `/` is the landing page. `/landing` opens that same page,
 *    and `/login` is the app sign-in.
 *
 * B. **Subdomain mode** (production at neighborli.net)
 *    Each portal is served from its own hostname:
 *        www.neighborli.net         → landing portal
 *        app.neighborli.net         → consumer app portal
 *        admin.neighborli.net       → admin portal
 *        advertiser.neighborli.net  → advertiser portal
 *    `getPortal()` inspects `window.location.hostname` and returns
 *    the matching portal. The other portals' route trees are not mounted.
 *
 *    `dev.neighborli.net`, `beta.neighborli.net`, and the Azure production
 *    host are whole environments, not portals. They stay in path-prefix
 *    mode so every portal is served from that one host. On every
 *    environment, `/` is the public landing page and the app login stays
 *    at `/login`. `/landing` still opens the same page. Admin and
 *    advertiser stay at `/admin` and `/advertiser-portal`.
 *
 * Local dev / preview: no subdomains available — force a portal via
 * `?portal=landing` (or admin / advertiser / consumer) which writes to
 * sessionStorage for the tab.
 *
 * ── ENV overrides ────────────────────────────────────────────────────
 * `REACT_APP_LANDING_HOSTNAME`    e.g. "www.neighborli.net"
 * `REACT_APP_APP_HOSTNAME`        e.g. "app.neighborli.net"
 * `REACT_APP_ADMIN_HOSTNAME`      e.g. "admin.neighborli.net"
 * `REACT_APP_ADVERTISER_HOSTNAME` e.g. "advertiser.neighborli.net"
 * (When set, exact-match takes precedence over the hostname-prefix
 *  heuristic — useful for staging hostnames.)
 */

export const PORTALS = Object.freeze({
  LANDING:    'landing',
  CONSUMER:   'consumer',
  ADMIN:      'admin',
  ADVERTISER: 'advertiser',
});

const OVERRIDE_KEY = 'portal_override';

// Pull env-configured hostnames (may be undefined during preview builds).
const LANDING_HOST    = (process.env.REACT_APP_LANDING_HOSTNAME    || '').toLowerCase();
const APP_HOST        = (process.env.REACT_APP_APP_HOSTNAME        || '').toLowerCase();
const ADMIN_HOST      = (process.env.REACT_APP_ADMIN_HOSTNAME      || '').toLowerCase();
const ADVERTISER_HOST = (process.env.REACT_APP_ADVERTISER_HOSTNAME || '').toLowerCase();

/**
 * Returns the portal the current tab is running in.
 * Priority: query param override > sessionStorage override >
 *           exact env hostname match > hostname prefix heuristic >
 *           consumer (default / preview fallback).
 */
/**
 * Sign-in stays on the current host for every environment.
 * The www marketing site does not mount `/login`, so its links open the beta app.
 */
export function appLoginUrl() {
  if (typeof window === 'undefined') return '/login';
  if (getPortal() === PORTALS.LANDING) return 'https://beta.neighborli.net/login';
  return '/login';
}

export function getPortal() {
  if (typeof window === 'undefined') return PORTALS.CONSUMER;

  // Query param override — dev/preview convenience (`?portal=landing`).
  try {
    const q = new URLSearchParams(window.location.search).get('portal');
    if (q) {
      const p = q.toLowerCase();
      if (Object.values(PORTALS).includes(p)) {
        sessionStorage.setItem(OVERRIDE_KEY, p);
        return p;
      }
    }
    const stored = sessionStorage.getItem(OVERRIDE_KEY);
    if (stored && Object.values(PORTALS).includes(stored)) return stored;
  } catch { /* storage unavailable */ }

  const host = (window.location.hostname || '').toLowerCase();

  // Exact env match (staging / non-standard hostnames).
  if (LANDING_HOST    && host === LANDING_HOST)    return PORTALS.LANDING;
  if (APP_HOST        && host === APP_HOST)        return PORTALS.CONSUMER;
  if (ADMIN_HOST      && host === ADMIN_HOST)      return PORTALS.ADMIN;
  if (ADVERTISER_HOST && host === ADVERTISER_HOST) return PORTALS.ADVERTISER;

  // Convention-over-config prefix heuristic.
  // `dev.` and `beta.` are environments, so they fall through to path-prefix mode.
  if (host.startsWith('www.'))          return PORTALS.LANDING;
  if (host.startsWith('app.'))          return PORTALS.CONSUMER;
  if (host.startsWith('admin.'))        return PORTALS.ADMIN;
  if (host.startsWith('advertiser.'))   return PORTALS.ADVERTISER;

  // Default: consumer (preserves preview / path-prefix-mode behaviour).
  return PORTALS.CONSUMER;
}

/**
 * `true` when the current tab is running in "single-portal" (subdomain)
 * mode — i.e. the other portals' routes are not mounted and cross-portal
 * links must render as absolute URLs to their hostname.
 */
export function isSubdomainMode() {
  if (typeof window === 'undefined') return false;
  // Any configured env hostname implies subdomain mode.
  if (LANDING_HOST || APP_HOST || ADMIN_HOST || ADVERTISER_HOST) return true;
  const host = (window.location.hostname || '').toLowerCase();
  return (
    host.startsWith('www.') ||
    host.startsWith('app.') ||
    host.startsWith('admin.') ||
    host.startsWith('advertiser.')
  );
}

/**
 * Build a link to a path inside another portal.
 *
 * In subdomain mode returns an absolute `https://<other-portal>.<domain>/<path>`.
 * In path-prefix mode returns just the local path so React Router handles it:
 *   landing   → /landing
 *   consumer  → /
 *   admin     → /admin/...
 *   advertiser→ /advertiser-portal/...
 */
export function portalUrl(targetPortal, path = '/') {
  const clean = path.startsWith('/') ? path : `/${path}`;

  if (!isSubdomainMode()) {
    // Path-prefix mode — reproduce the historical prefixes.
    if (targetPortal === PORTALS.ADMIN)
      return `/admin${clean === '/' ? '/dashboard' : clean}`;
    if (targetPortal === PORTALS.ADVERTISER)
      return `/advertiser-portal${clean === '/' ? '/dashboard' : clean}`;
    if (targetPortal === PORTALS.LANDING)
      return `/landing${clean === '/' ? '' : clean}`;
    // CONSUMER — no prefix, routes from root.
    return clean;
  }

  // Subdomain mode — build an absolute URL to the target portal.
  const proto = window.location.protocol;
  const currentHost = window.location.hostname;
  // Strip any known subdomain prefix to get the root domain.
  const root = currentHost.replace(/^(www|app|admin|advertiser)\./, '');

  const hostByPortal = {
    [PORTALS.LANDING]:    LANDING_HOST    || `www.${root}`,
    [PORTALS.CONSUMER]:   APP_HOST        || `app.${root}`,
    [PORTALS.ADMIN]:      ADMIN_HOST      || `admin.${root}`,
    [PORTALS.ADVERTISER]: ADVERTISER_HOST || `advertiser.${root}`,
  };
  const host = hostByPortal[targetPortal] || currentHost;
  return `${proto}//${host}${clean}`;
}
